export type BookDefaultStylesV1 = {
  version: 1;
  pagePaddingPx: number;     // outer page padding in PujaViewPage
  baseFontSizePx: number;    // default font size for blocks (when no override)
  gridGapPx: number;         // spacing between blocks (only when gridColumns is 2)
  /** 1 = single column (default), 2 = two-column puja-style grid */
  gridColumns?: 1 | 2;

  /**
   * Global visual defaults for PujaViewPage.
   * Individual book styles (and per-block overrides) can override these.
   */
  headerBackgroundColor?: string;
  headerTextColor?: string;
  titleColor?: string;
  defaultTextColor?: string;
  defaultBackgroundColor?: string;
  defaultLinkColor?: string;

  /**
   * Advanced defaults (same controls as PujaView edit modals).
   * These are applied as the base style for all header titles and lines.
   */
  defaultHeaderStyle?: {
    fontSize?: number;
    color?: string;
    backgroundColor?: string;
    fontFamily?: string;
    fontWeight?: number;
    fontStyle?: 'normal' | 'italic';
    textDecoration?: string;
    textAlign?: 'left' | 'center' | 'right' | 'justify';
    borderWidth?: number;
    borderStyle?: 'none' | 'solid' | 'dashed' | 'dotted' | 'double';
    borderColor?: string;
    borderRadius?: number;
    padding?: number;
    paddingLeft?: number;
    paddingRight?: number;
    paddingTop?: number;
    paddingBottom?: number;
    margin?: number;
    marginLeft?: number;
    marginRight?: number;
    marginTop?: number;
    marginBottom?: number;
  };

  defaultLineStyle?: {
    fontSize?: number;
    color?: string;
    backgroundColor?: string;
    fontFamily?: string;
    fontWeight?: number;
    fontStyle?: 'normal' | 'italic';
    textDecoration?: string;
    textAlign?: 'left' | 'center' | 'right' | 'justify';
    borderWidth?: number;
    borderStyle?: 'none' | 'solid' | 'dashed' | 'dotted' | 'double';
    borderColor?: string;
    borderRadius?: number;
    padding?: number;
    paddingLeft?: number;
    paddingRight?: number;
    paddingTop?: number;
    paddingBottom?: number;
    margin?: number;
    marginLeft?: number;
    marginRight?: number;
    marginTop?: number;
    marginBottom?: number;
    /**
     * Default grid span for lines (full/half).
     */
    span?: 12 | 24;
  };

  /**
   * Nested line defaults:
   * - level 1: first-level nested content (bidhi/sankalpa inside a puja line)
   * - level 2+: deeper nested content (bidhi inside bidhi, etc.)
   */
  defaultNestedLineStyle?: {
    fontSize?: number;
    color?: string;
    backgroundColor?: string;
    fontFamily?: string;
    fontWeight?: number;
    fontStyle?: 'normal' | 'italic';
    textDecoration?: string;
    textAlign?: 'left' | 'center' | 'right' | 'justify';
    borderWidth?: number;
    borderStyle?: 'none' | 'solid' | 'dashed' | 'dotted' | 'double';
    borderColor?: string;
    borderRadius?: number;
    padding?: number;
    paddingLeft?: number;
    paddingRight?: number;
    paddingTop?: number;
    paddingBottom?: number;
    margin?: number;
    marginLeft?: number;
    marginRight?: number;
    marginTop?: number;
    marginBottom?: number;
  };

  defaultDeepNestedLineStyle?: {
    fontSize?: number;
    color?: string;
    backgroundColor?: string;
    fontFamily?: string;
    fontWeight?: number;
    fontStyle?: 'normal' | 'italic';
    textDecoration?: string;
    textAlign?: 'left' | 'center' | 'right' | 'justify';
    borderWidth?: number;
    borderStyle?: 'none' | 'solid' | 'dashed' | 'dotted' | 'double';
    borderColor?: string;
    borderRadius?: number;
    padding?: number;
    paddingLeft?: number;
    paddingRight?: number;
    paddingTop?: number;
    paddingBottom?: number;
    margin?: number;
    marginLeft?: number;
    marginRight?: number;
    marginTop?: number;
    marginBottom?: number;
  };

  /**
   * Card (top header area) defaults in PujaViewPage.
   */
  defaultCardHeaderStyle?: {
    fontSize?: number;
    color?: string;
    backgroundColor?: string;
    fontFamily?: string;
    fontWeight?: number;
    fontStyle?: 'normal' | 'italic';
    textDecoration?: string;
    textAlign?: 'left' | 'center' | 'right' | 'justify';
    borderWidth?: number;
    borderStyle?: 'none' | 'solid' | 'dashed' | 'dotted' | 'double';
    borderColor?: string;
    borderRadius?: number;
    padding?: number;
    paddingLeft?: number;
    paddingRight?: number;
    paddingTop?: number;
    paddingBottom?: number;
  };

  defaultCardTitleStyle?: {
    fontSize?: number;
    color?: string;
    backgroundColor?: string;
    fontFamily?: string;
    fontWeight?: number;
    fontStyle?: 'normal' | 'italic';
    textDecoration?: string;
    textAlign?: 'left' | 'center' | 'right' | 'justify';
    borderWidth?: number;
    borderStyle?: 'none' | 'solid' | 'dashed' | 'dotted' | 'double';
    borderColor?: string;
    borderRadius?: number;
    padding?: number;
    paddingLeft?: number;
    paddingRight?: number;
    paddingTop?: number;
    paddingBottom?: number;
    margin?: number;
    marginLeft?: number;
    marginRight?: number;
    marginTop?: number;
    marginBottom?: number;
  };

  defaultTagStyle?: {
    fontSize?: number;
    color?: string;
    backgroundColor?: string;
    fontFamily?: string;
    fontWeight?: number;
    fontStyle?: 'normal' | 'italic';
    textDecoration?: string;
    textAlign?: 'left' | 'center' | 'right' | 'justify';
    borderWidth?: number;
    borderStyle?: 'none' | 'solid' | 'dashed' | 'dotted' | 'double';
    borderColor?: string;
    borderRadius?: number;
    padding?: number;
    paddingLeft?: number;
    paddingRight?: number;
    paddingTop?: number;
    paddingBottom?: number;
    margin?: number;
    marginLeft?: number;
    marginRight?: number;
    marginTop?: number;
    marginBottom?: number;
  };

  defaultLinkStyle?: {
    fontSize?: number;
    color?: string;
    backgroundColor?: string;
    fontFamily?: string;
    fontWeight?: number;
    fontStyle?: 'normal' | 'italic';
    textDecoration?: string;
    textAlign?: 'left' | 'center' | 'right' | 'justify';
    borderWidth?: number;
    borderStyle?: 'none' | 'solid' | 'dashed' | 'dotted' | 'double';
    borderColor?: string;
    borderRadius?: number;
    padding?: number;
    paddingLeft?: number;
    paddingRight?: number;
    paddingTop?: number;
    paddingBottom?: number;
  };

  /** Grantha reference blocks embedded in bidhi lines */
  defaultGranthaReferenceStyle?: BookDefaultStylesV1['defaultLineStyle'];
  /** Wrapper around linked sankalpa blocks */
  defaultSankalpaBlockStyle?: BookDefaultStylesV1['defaultLineStyle'];
  /** Alternate-language gloss (bhed) lines */
  defaultBhedStyle?: BookDefaultStylesV1['defaultLineStyle'];
};

const STORAGE_KEY = 'bookDefaultStyles:v1';

export const DEFAULT_STYLE_TEMPLATE_NAME = 'Karmakanda default';

/** One block per line — each mantra/header/ref starts on its own row. */
const BLOCK_TEXT_STYLE = {
  display: 'block' as const,
  whiteSpace: 'pre-wrap' as const,
  lineHeight: 1,
  marginTop: 0,
  marginBottom: 0,
  marginLeft: 0,
  marginRight: 0,
  padding: 0,
};

export const DEFAULT_BOOK_STYLES: BookDefaultStylesV1 = {
  version: 1,
  pagePaddingPx: 0,
  baseFontSizePx: 16,
  gridGapPx: 0,
  gridColumns: 1,
  headerBackgroundColor: '',
  headerTextColor: '',
  titleColor: '',
  defaultTextColor: '',
  defaultBackgroundColor: '',
  defaultLinkColor: '',
  defaultHeaderStyle: { ...BLOCK_TEXT_STYLE, fontWeight: 600 },
  defaultLineStyle: { ...BLOCK_TEXT_STYLE, span: 24 },
  defaultNestedLineStyle: { ...BLOCK_TEXT_STYLE },
  defaultDeepNestedLineStyle: { ...BLOCK_TEXT_STYLE },
  defaultCardHeaderStyle: undefined,
  defaultCardTitleStyle: undefined,
  defaultTagStyle: undefined,
  defaultLinkStyle: undefined,
  defaultGranthaReferenceStyle: { ...BLOCK_TEXT_STYLE },
  defaultSankalpaBlockStyle: { ...BLOCK_TEXT_STYLE },
  defaultBhedStyle: { ...BLOCK_TEXT_STYLE },
};

function mergeStyleBlock<T extends Record<string, unknown> | undefined>(
  base: T,
  override: T,
): T {
  if (!base && !override) return undefined as T;
  const merged = { ...(base || {}), ...(override || {}) } as Record<string, unknown>;
  // Karmakanda reader: keep rows tight; legacy Puja templates often set vertical margins.
  merged.marginTop = 0;
  merged.marginBottom = 0;
  merged.marginLeft = 0;
  merged.marginRight = 0;
  merged.padding = 0;
  merged.paddingTop = 0;
  merged.paddingBottom = 0;
  merged.paddingLeft = 0;
  merged.paddingRight = 0;
  if (typeof merged.margin === 'number' && merged.margin > 0) {
    merged.margin = 0;
  }
  return merged as T;
}

/** Fill missing fields from built-in defaults (keeps one consistent block-per-line layout). */
export function mergeBookDefaultStyles(partial?: BookDefaultStylesV1 | null): BookDefaultStylesV1 {
  if (!partial) return DEFAULT_BOOK_STYLES;
  return {
    ...DEFAULT_BOOK_STYLES,
    ...partial,
    defaultHeaderStyle: mergeStyleBlock(DEFAULT_BOOK_STYLES.defaultHeaderStyle, partial.defaultHeaderStyle),
    defaultLineStyle: mergeStyleBlock(DEFAULT_BOOK_STYLES.defaultLineStyle, partial.defaultLineStyle),
    defaultNestedLineStyle: mergeStyleBlock(
      DEFAULT_BOOK_STYLES.defaultNestedLineStyle,
      partial.defaultNestedLineStyle,
    ),
    defaultDeepNestedLineStyle: mergeStyleBlock(
      DEFAULT_BOOK_STYLES.defaultDeepNestedLineStyle,
      partial.defaultDeepNestedLineStyle,
    ),
    defaultGranthaReferenceStyle: mergeStyleBlock(
      DEFAULT_BOOK_STYLES.defaultGranthaReferenceStyle,
      partial.defaultGranthaReferenceStyle,
    ),
    defaultSankalpaBlockStyle: mergeStyleBlock(
      DEFAULT_BOOK_STYLES.defaultSankalpaBlockStyle,
      partial.defaultSankalpaBlockStyle,
    ),
    defaultBhedStyle: mergeStyleBlock(DEFAULT_BOOK_STYLES.defaultBhedStyle, partial.defaultBhedStyle),
    defaultLinkStyle: mergeStyleBlock(DEFAULT_BOOK_STYLES.defaultLinkStyle, partial.defaultLinkStyle),
  };
}

function isValidColorString(v: unknown): v is string {
  return typeof v === 'string';
}

function isFiniteNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

function asFiniteNumberOrUndefined(v: unknown): number | undefined {
  if (typeof v === 'string' && v.trim() && Number.isFinite(Number(v))) return Number(v);
  if (isFiniteNumber(v)) return v;
  return undefined;
}

function asStyleObject(v: unknown): Record<string, unknown> | undefined {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as any) : undefined;
}

function loadAdvancedStyle(parsedStyle: unknown): any | undefined {
  const s = asStyleObject(parsedStyle);
  if (!s) return undefined;
  return {
    fontSize: asFiniteNumberOrUndefined(s.fontSize),
    color: isValidColorString(s.color) ? s.color : undefined,
    backgroundColor: isValidColorString(s.backgroundColor) ? s.backgroundColor : undefined,
    fontFamily: isValidColorString(s.fontFamily) ? s.fontFamily : undefined,
    fontWeight: asFiniteNumberOrUndefined(s.fontWeight),
    fontStyle: s.fontStyle === 'italic' || s.fontStyle === 'normal' ? s.fontStyle : undefined,
    textDecoration: isValidColorString(s.textDecoration) ? s.textDecoration : undefined,
    textAlign:
      s.textAlign === 'left' || s.textAlign === 'center' || s.textAlign === 'right' || s.textAlign === 'justify'
        ? s.textAlign
        : undefined,
    borderWidth: asFiniteNumberOrUndefined(s.borderWidth),
    borderStyle:
      s.borderStyle === 'none' || s.borderStyle === 'solid' || s.borderStyle === 'dashed' || s.borderStyle === 'dotted' || s.borderStyle === 'double'
        ? s.borderStyle
        : undefined,
    borderColor: isValidColorString(s.borderColor) ? s.borderColor : undefined,
    borderRadius: asFiniteNumberOrUndefined(s.borderRadius),
    padding: asFiniteNumberOrUndefined(s.padding),
    paddingLeft: asFiniteNumberOrUndefined(s.paddingLeft),
    paddingRight: asFiniteNumberOrUndefined(s.paddingRight),
    paddingTop: asFiniteNumberOrUndefined(s.paddingTop),
    paddingBottom: asFiniteNumberOrUndefined(s.paddingBottom),
    margin: asFiniteNumberOrUndefined(s.margin),
    marginLeft: asFiniteNumberOrUndefined(s.marginLeft),
    marginRight: asFiniteNumberOrUndefined(s.marginRight),
    marginTop: asFiniteNumberOrUndefined(s.marginTop),
    marginBottom: asFiniteNumberOrUndefined(s.marginBottom),
    lineHeight: asFiniteNumberOrUndefined(s.lineHeight),
    display:
      s.display === 'block' || s.display === 'inline' || s.display === 'inline-block'
        ? s.display
        : undefined,
    whiteSpace:
      s.whiteSpace === 'pre-wrap' || s.whiteSpace === 'pre' || s.whiteSpace === 'normal'
        ? s.whiteSpace
        : undefined,
  };
}

export function loadBookDefaultStyles(): BookDefaultStylesV1 {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_BOOK_STYLES;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.version !== 1) return DEFAULT_BOOK_STYLES;
    const defaultLineStyle = loadAdvancedStyle(parsed.defaultLineStyle);
    const maybeSpan = (parsed?.defaultLineStyle && (parsed.defaultLineStyle as any).span) ?? undefined;
    const span: 12 | 24 | undefined = maybeSpan === 12 || maybeSpan === 24 ? maybeSpan : undefined;
    return {
      version: 1,
      pagePaddingPx: Number.isFinite(parsed.pagePaddingPx) ? Number(parsed.pagePaddingPx) : DEFAULT_BOOK_STYLES.pagePaddingPx,
      baseFontSizePx: Number.isFinite(parsed.baseFontSizePx) ? Number(parsed.baseFontSizePx) : DEFAULT_BOOK_STYLES.baseFontSizePx,
      gridGapPx: Number.isFinite(parsed.gridGapPx) ? Number(parsed.gridGapPx) : DEFAULT_BOOK_STYLES.gridGapPx,
      gridColumns: parsed.gridColumns === 2 ? 2 : 1,
      headerBackgroundColor: isValidColorString(parsed.headerBackgroundColor) ? parsed.headerBackgroundColor : '',
      headerTextColor: isValidColorString(parsed.headerTextColor) ? parsed.headerTextColor : '',
      titleColor: isValidColorString(parsed.titleColor) ? parsed.titleColor : '',
      defaultTextColor: isValidColorString(parsed.defaultTextColor) ? parsed.defaultTextColor : '',
      defaultBackgroundColor: isValidColorString(parsed.defaultBackgroundColor) ? parsed.defaultBackgroundColor : '',
      defaultLinkColor: isValidColorString(parsed.defaultLinkColor) ? parsed.defaultLinkColor : '',
      defaultHeaderStyle: loadAdvancedStyle(parsed.defaultHeaderStyle),
      defaultLineStyle: defaultLineStyle ? { ...defaultLineStyle, span } : undefined,
      defaultNestedLineStyle: loadAdvancedStyle(parsed.defaultNestedLineStyle),
      defaultDeepNestedLineStyle: loadAdvancedStyle(parsed.defaultDeepNestedLineStyle),
      defaultCardHeaderStyle: loadAdvancedStyle(parsed.defaultCardHeaderStyle),
      defaultCardTitleStyle: loadAdvancedStyle(parsed.defaultCardTitleStyle),
      defaultTagStyle: loadAdvancedStyle(parsed.defaultTagStyle),
      defaultLinkStyle: loadAdvancedStyle(parsed.defaultLinkStyle),
      defaultGranthaReferenceStyle: loadAdvancedStyle(parsed.defaultGranthaReferenceStyle),
      defaultSankalpaBlockStyle: loadAdvancedStyle(parsed.defaultSankalpaBlockStyle),
      defaultBhedStyle: loadAdvancedStyle(parsed.defaultBhedStyle),
    };
  } catch {
    return DEFAULT_BOOK_STYLES;
  }
}

export function saveBookDefaultStyles(next: BookDefaultStylesV1) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  // Let open PujaViewPage tabs react immediately (same-tab updates don't trigger `storage`).
  window.dispatchEvent(new Event('bookDefaultStylesUpdated'));
}

export function resetBookDefaultStyles() {
  localStorage.removeItem(STORAGE_KEY);
  window.dispatchEvent(new Event('bookDefaultStylesUpdated'));
}

